import { addToast } from '@octano/global-ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row } from 'reactstrap';

import { useValidateRetracted } from '../../../../admission/AdmissionsListEnrolled/hooks/useValidateRetracted';
import { desistStudyPlanChange } from '../../../../api/requests/tuitionProcess';
import { ModalConfirmation } from '../../../../components/modals/Confirmation';
import ConfirmationModal from '../../../../components/modals/ConfirmationModal';
import { ModalsValidateRetract } from '../../../../components/modals/ModalsValidateRetract';
import { Student } from '../../../../types/tuitionProcessOnSite';
import { showToast } from '../../../../utils/toast';
import {
  changeStatusToDesist,
  changeStatusToRetracted,
} from '../../AdmissionManagement/api';
import { useGoTo } from './hooks/useGoTo';
import { useActionForm } from './parts/useActionForm';

interface TuitionProcessFormProps {
  student: Student;
  preload: () => void;
  loading: boolean;
}

export function TuitionProcessFormActions({
  student,
  preload,
  loading,
}: TuitionProcessFormProps): JSX.Element {
  const prefix = 'tuitionProcess.form';
  const { t } = useTranslation();

  const { goToPostulation } = useGoTo({ student });
  const {
    getActionForm,
    modalConfirmOpen,
    setModalConfirmOpen,
    setSubmitLoad,
  } = useActionForm({
    admissionTypeId: student.admissionTypeId,
    postulanDetailId: student.Details_id,
  });

  const {
    studentName,
    selectRetractedID,
    setSelectRetractedID,
    showRetractedModal,
    setShowRetractedModal: setRetractedModal,
    validateRetracted,
  } = useValidateRetracted();

  const [confirmChangeEnrolled, setConfirmChangeEnrolled] = useState<boolean>(
    false,
  );
  const [openModalDesist, setOpenModalDesist] = useState<boolean>(false);

  const actionForm = getActionForm(
    student.newAdmission
      ? student.newAdmission.postulationDetailStatus
      : student.status,
    student.hasCampusesWithAccess,
    () =>
      student.Details_id &&
      validateRetracted(
        student.Details_id,
        student.names,
        `${student.lastnamePather} ${student.lastnameMother}`,
      ),
    () => setConfirmChangeEnrolled(true),
    () => goToPostulation({ fromStart: false }),
    () => setOpenModalDesist(true),
  );

  const onConfirmRetracted = async () => {
    setModalConfirmOpen(false);
    if (student.Details_id) {
      let { error } = await changeStatusToRetracted(student.Details_id, {
        showWarning: false,
      });
      if (error) {
        showToast(true, t);
      } else {
        showToast(false, t);
        preload();
      }
    }
  };

  const onConfirmDesisted = async () => {
    setModalConfirmOpen(false);
    if (student.Details_id) {
      let { error } = await changeStatusToDesist(student.Details_id);
      if (error) {
        showToast(true, t);
      } else {
        showToast(false, t);
        preload();
      }
    }
  };

  const onCloseDesisted = () => {
    setModalConfirmOpen(false);
  };

  const onConfirmDesist = async () => {
    if (student.newAdmission?.postulationDetailId) {
      const { data, error } = await desistStudyPlanChange(
        student.newAdmission?.postulationDetailId,
      );
      if (data) {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`${prefix}.desistAdmissionMessage`),
        });
      }
      if (error) {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t('common.errors.save'),
        });
      }
    } else {
      addToast({
        icon: 'error',
        color: 'danger',
        text: t('common.errors.save'),
      });
    }
    preload();
    onCloseModalDesist();
  };

  const onCloseModalDesist = () => {
    setOpenModalDesist(false);
  };

  useEffect(() => {
    setSubmitLoad(loading);
  }, [loading, setSubmitLoad]);

  return (
    <>
      <Row style={{ margin: 0 }}>
        <Col md={12}>{actionForm.actions}</Col>
      </Row>
      <ModalConfirmation
        open={modalConfirmOpen}
        title={t(`${prefix}.modalConfirm.title`)}
        subtitle={t(`${prefix}.modalConfirm.subtitle`)}
        onClose={onCloseDesisted}
        onConfirm={
          student?.status === 'en_proceso'
            ? onConfirmDesisted
            : onConfirmRetracted
        }
      />
      <ModalsValidateRetract
        updateQuery={() => preload()}
        studentName={studentName}
        selectRetractedID={selectRetractedID}
        setSelectRetractedID={setSelectRetractedID}
        showRetractedModal={showRetractedModal}
        setRetractedModal={setRetractedModal}
      />
      <ModalConfirmation
        open={confirmChangeEnrolled}
        title={t(`${prefix}.modalConfirmChange.title`)}
        subtitle={t(`${prefix}.modalConfirmChange.subtitle`)}
        onClose={() => setConfirmChangeEnrolled(false)}
        onConfirm={() => {
          setConfirmChangeEnrolled(false);
          goToPostulation({ fromStart: true });
        }}
      />
      <ConfirmationModal
        iconName="warning"
        isOpen={openModalDesist}
        toggle={onCloseModalDesist}
        title={t(`${prefix}.modalDesist.title`)}
        body={t(`${prefix}.modalDesist.subtitle`)}
        boxDetail={{
          title: t(`${prefix}.modalDesist.detailTitle`),
          body: [student.studyPlanVersion_name ?? ''],
        }}
        primaryBtn={{
          text: t(`common.actions.confirm`),
          action: onConfirmDesist,
        }}
        secondaryBtn={{
          text: t(`common.actions.cancel`),
          action: onCloseModalDesist,
        }}
      />
    </>
  );
}
